import React, { useRef } from "react";
import { Eye, Target, Gem } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap, splitWords } from "@/lib/motion";

const ICONS = [Eye, Target, Gem];

/* A statement, read into focus: each word of the company's promise lights up
   as it crosses the reading line. Pillars follow as a quiet three-column spec. */
export default function AboutSection() {
  const { t, lang } = useLang();
  const root = useRef(null);
  const words = splitWords(t.about.body);

  useGsap(
    () => {
      gsap.fromTo(
        ".about-word",
        { opacity: 0.16 },
        {
          opacity: 1,
          stagger: 0.05,
          ease: "none",
          scrollTrigger: { trigger: ".about-statement", start: "top 78%", end: "bottom 45%", scrub: true },
        },
      );
      gsap.from(".about-pillar", {
        y: 28,
        opacity: 0,
        duration: 0.9,
        stagger: 0.1,
        scrollTrigger: { trigger: ".about-pillars", start: "top 82%", once: true },
      });
      gsap.from(".about-rule", {
        scaleX: 0,
        duration: 1.2,
        stagger: 0.1,
        ease: "expo.inOut",
        scrollTrigger: { trigger: ".about-pillars", start: "top 85%", once: true },
      });
    },
    [lang],
    root,
  );

  return (
    <section ref={root} data-testid="about-section" className="relative bg-[var(--bg)] text-[var(--ink)] py-28 lg:py-40">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10">
          <h2 className="lg:col-span-4 type-display text-[clamp(1.8rem,3vw,2.6rem)] max-w-[14ch]">{t.about.title}</h2>
          <p className="about-statement lg:col-span-8 text-[clamp(1.5rem,2.7vw,2.4rem)] leading-[1.32] font-semibold tracking-[-0.01em] max-w-[34ch]">
            {words.map((w, i) =>
              /^\s+$/.test(w) ? (
                w
              ) : (
                <span key={`${lang}-${i}`} className="about-word">
                  {w}
                </span>
              ),
            )}
          </p>
        </div>

        <div className="about-pillars mt-20 lg:mt-28 grid gap-10 md:grid-cols-3">
          {t.about.pillars.map((p, i) => {
            const Icon = ICONS[i] || Eye;
            return (
              <div key={p.t} className="about-pillar" data-testid={`about-pillar-${i}`}>
                <div className="about-rule h-px bg-[var(--line)] origin-left rtl:origin-right" />
                <div className="mt-6 flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[var(--accent-ink)]" aria-hidden />
                  <h3 className="text-lg font-extrabold">{p.t}</h3>
                </div>
                <p className="mt-3 text-[15px] leading-relaxed text-[var(--muted-ink)] max-w-[36ch]">{p.d}</p>
              </div>
            );
          })}
        </div>

        <p className="mt-16 flex flex-wrap items-center gap-3 text-sm font-semibold text-[var(--muted-ink)]">
          <span className="w-10 h-[2px] bg-[var(--accent-ink)]" aria-hidden />
          {t.cities.turkey} <span aria-hidden>·</span> {t.cities.china} <span aria-hidden>{lang === "ar" ? "←" : "→"}</span> {t.cities.syria}
        </p>
      </div>
    </section>
  );
}
